import { support } from '../../../api';
import { call, takeLatest, put, delay } from 'redux-saga/effects';
import {
  FETCH_FAILED_REDEMPTIONS,
  FETCH_FAILED_REDEMPTIONS_START,
  FETCH_FAILED_REDEMPTIONS_COMPLETE,
  FETCH_FAILED_REDEMPTIONS_ERROR,
  BATCH_RETRY_REDEEM_REWARD,
  BATCH_RETRY_REDEEM_REWARD_START,
  BATCH_RETRY_REDEEM_REWARD_PROGRESS,
  BATCH_RETRY_REDEEM_REWARD_COMPLETE,
  BATCH_RETRY_REDEEM_REWARD_ERROR,
  FETCH_RETRY_REDEEM_JOB_STATUS,
  FETCH_MERGE_WALLET_DATA,
  FETCH_MERGE_WALLET_DATA_START,
  FETCH_MERGE_WALLET_DATA_COMPLETE,
  FETCH_MERGE_WALLET_DATA_ERROR,
  MERGE_WALLET,
  MERGE_WALLET_START,
  MERGE_WALLET_COMPLETE,
  MERGE_WALLET_ERROR,
  FETCH_CAREGIVER_BY_PHONE,
  FETCH_CAREGIVER_BY_PHONE_START,
  FETCH_CAREGIVER_BY_PHONE_COMPLETE,
  FETCH_CAREGIVER_BY_PHONE_ERROR,
  CHANGE_EMPLOYEE_PASSWORD,
  CHANGE_EMPLOYEE_PASSWORD_START,
  CHANGE_EMPLOYEE_PASSWORD_COMPLETE,
  CHANGE_EMPLOYEE_PASSWORD_ERROR,
  FETCH_REWARD_CONFIG_LIST,
  FETCH_REWARD_CONFIG_LIST_START,
  FETCH_REWARD_CONFIG_LIST_COMPLETE,
  FETCH_REWARD_CONFIG_LIST_ERROR,
  MANUAL_REDEMPTION,
  MANUAL_REDEMPTION_START,
  MANUAL_REDEMPTION_COMPLETE,
  MANUAL_REDEMPTION_ERROR,
  UPDATE_PRIMARY_PHONE,
  UPDATE_PRIMARY_PHONE_START,
  UPDATE_PRIMARY_PHONE_COMPLETE,
  UPDATE_PRIMARY_PHONE_ERROR,
  FETCH_TANGO_TOTAL_BALANCE,
  FETCH_TANGO_TOTAL_BALANCE_START,
  FETCH_TANGO_TOTAL_BALANCE_COMPLETE,
  FETCH_TANGO_TOTAL_BALANCE_ERROR,
} from './actions';
import errorStrings from '../../../constants/errorStrings';
import { RETRY_JOB_STORAGE_KEY, RETRY_JOB_POLL_DELAY_MS } from '../../../constants/Constants';
import { setUserStatusUnauthorised } from '../userProfile/actions';

const getNestedData = (payload) => {
  if (!payload) return {};
  const firstLevel = payload?.data || payload;
  const secondLevel = firstLevel?.data || firstLevel;
  return secondLevel || {};
};

const CARE_HEROES_LABEL = 'care heroes';

const normalizeAgencyLabel = (value) => String(value || '').trim().toLowerCase();

const isCareHeroesAccount = (account = {}) => {
  const agencyId = normalizeAgencyLabel(account.agencyId ?? account.agency_id);
  const displayName = normalizeAgencyLabel(account.displayName ?? account.display_name);
  return agencyId === CARE_HEROES_LABEL || displayName === CARE_HEROES_LABEL;
};

const parseTangoBalance = (raw) => {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const getCareHeroesTangoBalance = (payload = {}) => {
  const accounts = payload?.accounts;
  if (!Array.isArray(accounts)) {
    return 0;
  }
  const careHeroesAccount = accounts.find(isCareHeroesAccount);
  if (!careHeroesAccount) {
    return 0;
  }
  return parseTangoBalance(careHeroesAccount.balance);
};

const mockRetryPollAttemptsByTask = {};

const getRetryJobStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return [];
  }
  try {
    const data = window.localStorage.getItem(RETRY_JOB_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    return [];
  }
};

const setRetryJobStorage = (jobs = []) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    if (!jobs.length) {
      window.localStorage.removeItem(RETRY_JOB_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(RETRY_JOB_STORAGE_KEY, JSON.stringify(jobs));
  } catch (e) {
    // noop
  }
};

const upsertRetryJobStorage = (job) => {
  const jobs = getRetryJobStorage();
  const filteredJobs = jobs.filter((item) => item.taskId !== job.taskId);
  setRetryJobStorage([...filteredJobs, job]);
};

const removeRetryJobStorage = (taskId) => {
  const jobs = getRetryJobStorage();
  setRetryJobStorage(jobs.filter((item) => item.taskId !== taskId));
};

const isValidTaskId = (taskId) => typeof taskId === 'string'
  ? taskId.trim().length > 0
  : taskId !== null && taskId !== undefined;

const normalizeStatusPayload = (payload = {}) => {
  const taskId = payload?.taskId || payload?.task_id;
  const jobStatus = String(payload?.job_status || payload?.jobStatus || '')
    .trim()
    .toUpperCase();
  const orderDetails = payload?.orderDetails || {};
  const completedIds = orderDetails?.completed || orderDetails?.Completed || [];
  const failedIds = orderDetails?.failed || orderDetails?.Failed || [];
  const pendingIds = orderDetails?.pending || orderDetails?.Pending || [];
  const successCount = completedIds.length;
  const failedCount = failedIds.length;
  const completedPercentage = Number(payload?.completed_percentage ?? payload?.completedPercentage ?? 0);
  const isTerminalStatus = ['COMPLETED', 'COMPLETE', 'FAILED', 'FAILURE'].includes(jobStatus);
  const isComplete = isTerminalStatus || (completedPercentage >= 100 && pendingIds.length === 0);
  const finalStatus = !isComplete
    ? 'in_progress'
    : jobStatus === 'FAILED'
      ? 'failed'
    : successCount > 0 && failedCount > 0
      ? 'partial_success'
      : successCount > 0
        ? 'success'
        : 'failed';

  return {
    taskId,
    jobStatus,
    isComplete,
    completedPercentage,
    orderDetails: {
      completed: completedIds,
      failed: failedIds,
      pending: pendingIds,
    },
    successCount,
    failedCount,
    pendingCount: pendingIds.length,
    finalStatus,
  };
};

function* pollRetryRedeemJobStatus({ taskId }) {
  while (true) {
    const { response, error } = yield call(support.fetchRetryRedeemJobStatus, {
      params: { task_id: taskId },
    });

    if (response) {
      const retryStatusPayload = normalizeStatusPayload(getNestedData(response));
      yield put({
        type: BATCH_RETRY_REDEEM_REWARD_PROGRESS,
        payload: {
          data: retryStatusPayload,
          pendingRewardOrderIds: retryStatusPayload.orderDetails.pending || [],
        },
      });
      if (retryStatusPayload.jobStatus === 'COMPLETED' || retryStatusPayload.jobStatus === 'FAILED') {
        removeRetryJobStorage(taskId);
      }
      if (retryStatusPayload.isComplete) {
        delete mockRetryPollAttemptsByTask[taskId];
        return { response: retryStatusPayload };
      }
    } else if (error) {
      return { error };
    }

    yield delay(RETRY_JOB_POLL_DELAY_MS);
  }
}

export function* fetchFailedRedemptions({ payload: { params } }) {
  try {
    yield put({ type: FETCH_FAILED_REDEMPTIONS_START });
    
    const { response, error } = yield call(support.fetchFailedRedemptions, { params });
    if (response) {
      yield put({
        type: FETCH_FAILED_REDEMPTIONS_COMPLETE,
        payload: {
          data: response.data.data,
          totalPages: response.data.total_pages,
          totalRecords: response.data.total_records,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onFetchFailedRedemptionsError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(onFetchFailedRedemptionsError({ error: errorStrings.commonError }));
  }
}

export function* batchRetryRedeemReward({ payload: { params } }) {
  try {
    const rewardOrderIds = params?.rewardOrderIds || [];
    yield put({
      type: BATCH_RETRY_REDEEM_REWARD_START,
      payload: {
        rewardOrderIds,
      },
    });
    const { response, error } = yield call(support.batchRetryRedeemReward, { params });
    if (response) {
      const responsePayload = getNestedData(response);
      const taskId = responsePayload?.task_id || responsePayload?.taskId;

      if (isValidTaskId(taskId)) {
        upsertRetryJobStorage({
          taskId,
          rewardOrderIds,
          createdAt: Date.now(),
        });
        const { response: statusResponse, error: statusError } = yield call(pollRetryRedeemJobStatus, {
          taskId,
        });
        
        if (statusError) {
          yield put(
            onBatchRetryRedeemRewardError({
              error: statusError?.data?.message
                ? statusError?.data?.message
                : errorStrings.commonError,
              rewardOrderIds,
            }),
          );
          return;
        }
        const finalStatusData = normalizeStatusPayload(statusResponse || {});
        removeRetryJobStorage(taskId);
        yield put({
          type: BATCH_RETRY_REDEEM_REWARD_COMPLETE,
          payload: {
            data: {
              ...finalStatusData,
              taskId,
            },
            rewardOrderIds,
            pendingRewardOrderIds: finalStatusData.orderDetails.pending || [],
          },
        });
        return;
      }

      yield put(
        onBatchRetryRedeemRewardError({
          error: 'Retry task id not found in response.',
          rewardOrderIds,
        }),
      );
      return;
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onBatchRetryRedeemRewardError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
            rewardOrderIds,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onBatchRetryRedeemRewardError({
        error: errorStrings.commonError,
        rewardOrderIds: params?.rewardOrderIds || [],
      }),
    );
  }
}

export function* fetchRetryRedeemJobStatus() {
  try {
    const jobs = getRetryJobStorage();
    for (const job of jobs) {
      const rewardOrderIds = job?.rewardOrderIds || [];
      const taskId = job?.taskId;
      if (!isValidTaskId(taskId)) {
        continue;
      }

      yield put({
        type: BATCH_RETRY_REDEEM_REWARD_START,
        payload: { rewardOrderIds },
      });

      const { response: statusResponse, error: statusError } = yield call(pollRetryRedeemJobStatus, {
        taskId,
      });

      if (statusError) {
        yield put(
          onBatchRetryRedeemRewardError({
            error: 'Retry job failed. Try again later.',
            rewardOrderIds,
          }),
        );
        continue;
      }

      const finalStatusData = normalizeStatusPayload(statusResponse || {});
      if (!finalStatusData.isComplete) {
        continue;
      }
      removeRetryJobStorage(taskId);
      yield put({
        type: BATCH_RETRY_REDEEM_REWARD_COMPLETE,
        payload: {
          data: {
            ...finalStatusData,
            taskId,
          },
          rewardOrderIds,
          pendingRewardOrderIds: finalStatusData.orderDetails.pending,
        },
      });
    }
  } catch (error) {
    yield put(
      onBatchRetryRedeemRewardError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* fetchMergeWalletData({ payload: { params } }) {
  try {
    yield put({ type: FETCH_MERGE_WALLET_DATA_START });
    const { response, error } = yield call(support.fetchMergeWalletData, { params });

    if (response) {
      yield put({
        type: FETCH_MERGE_WALLET_DATA_COMPLETE,
        payload: {
          data: response.data,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onFetchMergeWalletDataError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(onFetchMergeWalletDataError({ error: errorStrings.commonError }));
  }
}

export function* mergeWallet({ payload: { params } }) {
  try {
    yield put({ type: MERGE_WALLET_START });
    const { response, error } = yield call(support.mergeWallet, { params });

    if (response) {
      yield put({
        type: MERGE_WALLET_COMPLETE,
        payload: {
          data: response.data,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onMergeWalletError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onMergeWalletError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* fetchCaregiverByPhone({ payload: { params } }) {
  try {
    yield put({ type: FETCH_CAREGIVER_BY_PHONE_START });
    const { response, error } = yield call(support.fetchCaregiverByPhone, { params });
    
    // Mock response for testing - comment out the API call above and uncomment below to use mock data
    // const response = {
    //   data: {
    //     data: [
    //       {
    //         _id: "63b2c05ecc8e5b3c125059a0",
    //         firstName: "John",
    //         lastName: "Doe",
    //         phone: "+12674674451",
    //         email: "john.doe@example.com",
    //         role: "ROLE_PAID_CARE_GIVER",
    //         isMarkedDeleted: false,
    //         agency: "ALongTermCompanion",
    //       }
    //     ],
    //     total_records: 1,
    //     total_pages: 1,
    //     current_page: 1,
    //   }
    // };
    // const error = null;

    if (response) {
      // Extract the array of caregivers from response.data.data
      // Response structure: { data: { data: [...], total_records: 1, total_pages: 1, current_page: 1 } }
      const caregivers = response.data?.data || [];
      // Use the first caregiver if array exists and has items
      const caregiverData = Array.isArray(caregivers) && caregivers.length > 0 ? caregivers[0] : null;
      
      yield put({
        type: FETCH_CAREGIVER_BY_PHONE_COMPLETE,
        payload: {
          data: caregiverData,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onFetchCaregiverByPhoneError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(onFetchCaregiverByPhoneError({ error: errorStrings.commonError }));
  }
}

export function* changeEmployeePassword({ payload: { params } }) {
  try {
    yield put({ type: CHANGE_EMPLOYEE_PASSWORD_START });
    const { response, error } = yield call(support.changeEmployeePassword, { params });

    if (response) {
      yield put({
        type: CHANGE_EMPLOYEE_PASSWORD_COMPLETE,
        payload: {
          data: response.data,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onChangeEmployeePasswordError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(onChangeEmployeePasswordError({ error: errorStrings.commonError }));
  }
}

export function* fetchRewardConfigList({ payload = {} } = {}) {
  try {
    const params = payload?.params || {};
    yield put({ type: FETCH_REWARD_CONFIG_LIST_START });
    const { response, error } = yield call(support.fetchRewardConfigList, { params });

    if (response) {
      yield put({
        type: FETCH_REWARD_CONFIG_LIST_COMPLETE,
        payload: {
          data: response?.data || [],
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onFetchRewardConfigListError({
            error: error?.data?.message ? error?.data?.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(onFetchRewardConfigListError({ error: errorStrings.commonError }));
  }
}

export function* manualRedemption({ payload: { params } }) {
  try {
    yield put({ type: MANUAL_REDEMPTION_START });
    const { response, error } = yield call(support.manualRedemption, { params });

    if (response) {
      yield put({
        type: MANUAL_REDEMPTION_COMPLETE,
        payload: {
          data: response?.data || response,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onManualRedemptionError({
            error: error?.data?.message ? error?.data?.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(onManualRedemptionError({ error: errorStrings.commonError }));
  }
}

export function* updatePrimaryPhone({ payload: { params } }) {
  try {
    yield put({ type: UPDATE_PRIMARY_PHONE_START });
    const { response, error } = yield call(support.updatePrimaryPhone, { params });

    if (response) {
      yield put({
        type: UPDATE_PRIMARY_PHONE_COMPLETE,
        payload: {
          data: response?.data || response,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onUpdatePrimaryPhoneError({
            error: error?.data?.message ? error?.data?.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(onUpdatePrimaryPhoneError({ error: errorStrings.commonError }));
  }
}

export function* fetchTangoTotalBalance() {
  try {
    yield put({ type: FETCH_TANGO_TOTAL_BALANCE_START });
    const { response, error } = yield call(support.fetchTangoTotalBalance);

    if (response) {
      const nested = getNestedData(response);
      const totalBalance = getCareHeroesTangoBalance(nested);
      yield put({
        type: FETCH_TANGO_TOTAL_BALANCE_COMPLETE,
        payload: { totalBalance },
      });
    } else if (error?.status === 401) {
      yield put(setUserStatusUnauthorised());
      yield put(
        onFetchTangoTotalBalanceError({
          error: error?.data?.message ? error?.data?.message : errorStrings.commonError,
        }),
      );
    } else {
      yield put(
        onFetchTangoTotalBalanceError({
          error: error?.data?.message ? error?.data?.message : errorStrings.commonError,
        }),
      );
    }
  } catch (error) {
    yield put(onFetchTangoTotalBalanceError({ error: errorStrings.commonError }));
  }
}

const onFetchFailedRedemptionsError = (payload = {}) => ({
  type: FETCH_FAILED_REDEMPTIONS_ERROR,
  payload,
});

const onBatchRetryRedeemRewardError = (payload = {}) => ({
  type: BATCH_RETRY_REDEEM_REWARD_ERROR,
  payload,
});

const onFetchMergeWalletDataError = (payload = {}) => ({
  type: FETCH_MERGE_WALLET_DATA_ERROR,
  payload,
});

const onMergeWalletError = (payload = {}) => ({
  type: MERGE_WALLET_ERROR,
  payload,
});

const onFetchCaregiverByPhoneError = (payload = {}) => ({
  type: FETCH_CAREGIVER_BY_PHONE_ERROR,
  payload,
});

const onChangeEmployeePasswordError = (payload = {}) => ({
  type: CHANGE_EMPLOYEE_PASSWORD_ERROR,
  payload,
});

const onFetchRewardConfigListError = (payload = {}) => ({
  type: FETCH_REWARD_CONFIG_LIST_ERROR,
  payload,
});

const onManualRedemptionError = (payload = {}) => ({
  type: MANUAL_REDEMPTION_ERROR,
  payload,
});

const onUpdatePrimaryPhoneError = (payload = {}) => ({
  type: UPDATE_PRIMARY_PHONE_ERROR,
  payload,
});

const onFetchTangoTotalBalanceError = (payload = {}) => ({
  type: FETCH_TANGO_TOTAL_BALANCE_ERROR,
  payload,
});

function* watchFetchFailedRedemptions() {
  yield takeLatest(FETCH_FAILED_REDEMPTIONS, fetchFailedRedemptions);
}

function* watchBatchRetryRedeemReward() {
  yield takeLatest(BATCH_RETRY_REDEEM_REWARD, batchRetryRedeemReward);
}

function* watchFetchRetryRedeemJobStatus() {
  yield takeLatest(FETCH_RETRY_REDEEM_JOB_STATUS, fetchRetryRedeemJobStatus);
}

function* watchFetchMergeWalletData() {
  yield takeLatest(FETCH_MERGE_WALLET_DATA, fetchMergeWalletData);
}

function* watchMergeWallet() {
  yield takeLatest(MERGE_WALLET, mergeWallet);
}

function* watchFetchCaregiverByPhone() {
  yield takeLatest(FETCH_CAREGIVER_BY_PHONE, fetchCaregiverByPhone);
}

function* watchChangeEmployeePassword() {
  yield takeLatest(CHANGE_EMPLOYEE_PASSWORD, changeEmployeePassword);
}

function* watchFetchRewardConfigList() {
  yield takeLatest(FETCH_REWARD_CONFIG_LIST, fetchRewardConfigList);
}

function* watchManualRedemption() {
  yield takeLatest(MANUAL_REDEMPTION, manualRedemption);
}

function* watchUpdatePrimaryPhone() {
  yield takeLatest(UPDATE_PRIMARY_PHONE, updatePrimaryPhone);
}

function* watchFetchTangoTotalBalance() {
  yield takeLatest(FETCH_TANGO_TOTAL_BALANCE, fetchTangoTotalBalance);
}

export const sagas = [
  watchFetchFailedRedemptions,
  watchBatchRetryRedeemReward,
  watchFetchRetryRedeemJobStatus,
  watchFetchMergeWalletData,
  watchMergeWallet,
  watchFetchCaregiverByPhone,
  watchChangeEmployeePassword,
  watchFetchRewardConfigList,
  watchManualRedemption,
  watchUpdatePrimaryPhone,
  watchFetchTangoTotalBalance,
];

